import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchBasket } from '@state/basket';
import { BookingApi, PromoCodeResponse } from '@model/iceberg';
import { PromoCodeState } from '@model/state/promo-code-state';
import { DealFinderResultsActions } from '@state/slices/deal-finder-results/dealFinderResultsSlice';

export enum PromoCodeActions {
  SLICE_NAME = '@PROMO_CODE',
  SUBMIT_PROMO_CODE = '@PROMO_CODE/submitPromoCode'
}

export const INITIAL_PROMO_CODE_STATE: PromoCodeState = {
  input: '',
  error: false
};

export const submitPromoCode = createAsyncThunk<void, { token: string; input: string }, { rejectValue: string }>(
  PromoCodeActions.SUBMIT_PROMO_CODE,
  async ({ token, input }, { dispatch, rejectWithValue }) => {
    if (!input) {
      return rejectWithValue('Promo code input is required');
    }

    try {
      const bookingApi = new BookingApi();
      const response: PromoCodeResponse = await bookingApi.promoCode(token, input);

      if (!response.data.valid) {
        return rejectWithValue(response.data.error || 'Invalid promo code');
      }

      dispatch(fetchBasket(token));

      return;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to submit promo code';
      return rejectWithValue(errorMessage);
    }
  }
);

const promoCodeSlice = createSlice({
  name: PromoCodeActions.SLICE_NAME,
  initialState: INITIAL_PROMO_CODE_STATE,
  reducers: {
    setPromoCodeInput: (state, action: PayloadAction<string>) => {
      state.input = action.payload;
    },
    setPromoCodeError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload;
    },
    clearPromoCodeInput: (state) => {
      state.input = '';
    },
    resetPromoCode: () => {
      return INITIAL_PROMO_CODE_STATE;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitPromoCode.pending, (state) => {
        state.error = false;
      })
      .addCase(submitPromoCode.fulfilled, (state) => {
        state.input = '';
        state.error = false;
      })
      .addCase(submitPromoCode.rejected, (state) => {
        state.error = true;
      })
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, () => {
        return INITIAL_PROMO_CODE_STATE;
      });
  }
});

export const { setPromoCodeInput, setPromoCodeError, clearPromoCodeInput, resetPromoCode } = promoCodeSlice.actions;

export const promoCodeReducer = promoCodeSlice.reducer;
